'use client';
import React from 'react';
import type { MindmapLayoutDirection, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarItem,
  MenubarSeparator,
} from '@core/ui/primitives/menubar';
import { Button } from '@/core/ui/primitives/button';
type StylePatch = Partial<Pick<ToolbarSelectedSnapshot, 'textColor' | 'bgColor' | 'borderColor' | 'shape'>>;
interface ToolbarSelectedSnapshot {
  textColor: string;
  bgColor: string;
  borderColor: string;
  shape: MindmapNodeShape;
}
export interface NodeStyleMenubarProps {
  isRoot?: boolean;
  layoutDirection?: MindmapLayoutDirection;
  selected?: Partial<ToolbarSelectedSnapshot>;
  onPreview?: (style: StylePatch) => void;
  onCommit?: (style: StylePatch) => void;
  onChangeDirection?: (direction: MindmapLayoutDirection) => void;
  onPreviewDirection?: (direction: MindmapLayoutDirection) => void;
  onResetDirectionPreview?: () => void;
  disabled?: boolean;
  selectedId?: string;
}
const SWATCHES = ['#ffffff', '#6b7280', '#111827', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];
type MenuKey = 'text' | 'bg' | 'border' | 'shape' | 'direction';
export function NodeStyleMenubar({
  isRoot,
  layoutDirection = 'right',
  selected,
  onPreview,
  onCommit,
  onChangeDirection,
  onPreviewDirection,
  onResetDirectionPreview,
  disabled,
  selectedId,
}: NodeStyleMenubarProps) {
  const origRef = React.useRef<ToolbarSelectedSnapshot | null>(null);
  const closingFromCommitRef = React.useRef(false);
  const [openMenu, setOpenMenu] = React.useState<MenuKey | null>(null);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const ensureSnapshot = () => {
    if (!origRef.current) {
      origRef.current = {
        textColor: selected?.textColor ?? '#111827',
        bgColor: selected?.bgColor ?? '#ffffff',
        borderColor: selected?.borderColor ?? '#9ca3af',
        shape: (selected?.shape as MindmapNodeShape) ?? 'rounded',
      };
    }
  };
  React.useEffect(() => {
    origRef.current = null;
  }, [selectedId]);
  const resetPreview = () => {
    const s = origRef.current;
    if (!s) return;
    onPreview?.({ textColor: s.textColor, bgColor: s.bgColor, borderColor: s.borderColor, shape: s.shape });
    onResetDirectionPreview?.();
  };
  const mergeSnapshot = (patch: StylePatch) => {
    ensureSnapshot();
    if (!origRef.current) return;
    (Object.keys(patch) as Array<keyof StylePatch>).forEach((key) => {
      const value = patch[key];
      if (value !== undefined) {
        (origRef.current as any)[key] = value;
      }
    });
  };
  const commitStyleAndClose = (patch: StylePatch) => {
    mergeSnapshot(patch);
    closingFromCommitRef.current = true;
    onCommit?.(patch);
    setOpenMenu(null);
  };
  const commitDirectionAndClose = (direction: MindmapLayoutDirection) => {
    closingFromCommitRef.current = true;
    onChangeDirection?.(direction);
    setOpenMenu(null);
  };
  const isActive = (menu: typeof openMenu, index: number) => openMenu === menu && activeIndex === index;
  const menuOrder = React.useMemo<MenuKey[]>(() => {
    const base: MenuKey[] = ['text', 'bg', 'border', 'shape'];
    if (isRoot) base.push('direction');
    return base;
  }, [isRoot]);
  const handleMenubarValueChange = React.useCallback(
    (nextValue: string) => {
      if (!nextValue) {
        setOpenMenu(null);
        return;
      }
      if (menuOrder.includes(nextValue as MenuKey)) {
        setOpenMenu(nextValue as MenuKey);
      } else {
        setOpenMenu(null);
      }
    },
    [menuOrder]
  );
  const getItemCount = (menu: typeof openMenu) => {
    if (!menu) return 0;
    switch (menu) {
      case 'text':
      case 'bg':
      case 'border':
        return SWATCHES.length;
      case 'shape':
        return 4;
      case 'direction':
        return 4;
      default:
        return 0;
    }
  };
  const previewAt = (menu: typeof openMenu, index: number) => {
    if (!menu) return;
    switch (menu) {
      case 'text':
        onPreview?.({ textColor: SWATCHES[(index + SWATCHES.length) % SWATCHES.length] });
        return;
      case 'bg':
        onPreview?.({ bgColor: SWATCHES[(index + SWATCHES.length) % SWATCHES.length] });
        return;
      case 'border':
        onPreview?.({ borderColor: SWATCHES[(index + SWATCHES.length) % SWATCHES.length] });
        return;
      case 'shape': {
        const shapes: MindmapNodeShape[] = ['rounded', 'rectangle', 'pill', 'underline'];
        onPreview?.({ shape: shapes[(index + shapes.length) % shapes.length] });
        return;
      }
      case 'direction': {
        const dirs: MindmapLayoutDirection[] = ['left', 'right', 'up', 'down'];
        onPreviewDirection?.(dirs[(index + dirs.length) % dirs.length]);
        return;
      }
    }
  };
  const commitAt = (menu: typeof openMenu, index: number) => {
    if (!menu) return;
    switch (menu) {
      case 'text':
        commitStyleAndClose({ textColor: SWATCHES[(index + SWATCHES.length) % SWATCHES.length] });
        return;
      case 'bg':
        commitStyleAndClose({ bgColor: SWATCHES[(index + SWATCHES.length) % SWATCHES.length] });
        return;
      case 'border':
        commitStyleAndClose({ borderColor: SWATCHES[(index + SWATCHES.length) % SWATCHES.length] });
        return;
      case 'shape': {
        const shapes: MindmapNodeShape[] = ['rounded', 'rectangle', 'pill', 'underline'];
        commitStyleAndClose({ shape: shapes[(index + shapes.length) % shapes.length] });
        return;
      }
      case 'direction': {
        const dirs: MindmapLayoutDirection[] = ['left', 'right', 'up', 'down'];
        commitDirectionAndClose(dirs[(index + dirs.length) % dirs.length]);
        return;
      }
    }
  };
  React.useEffect(() => {
    // Reset active index when switching menus
    setActiveIndex(0);
    if (openMenu) {
      ensureSnapshot();
      closingFromCommitRef.current = false;
    } else {
      if (closingFromCommitRef.current) {
        closingFromCommitRef.current = false;
      } else {
        resetPreview();
      }
      origRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenu]);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Meta+S to open styles with first (text) menu
      if ((e.key === 's' || e.key === 'S') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
        setOpenMenu('text');
        setActiveIndex(0);
        return;
      }
      // Skip if no menu is open
      if (!openMenu) return;
      const code = e.key;
      if (
        code === 'ArrowUp' ||
        code === 'ArrowDown' ||
        code === 'ArrowLeft' ||
        code === 'ArrowRight' ||
        code === 'Enter'
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (code === 'ArrowUp' || code === 'ArrowDown') {
        const count = getItemCount(openMenu);
        if (!count) return;
        const delta = code === 'ArrowDown' ? 1 : -1;
        const next = (activeIndex + delta + count) % count;
        setActiveIndex(next);
        previewAt(openMenu, next);
        return;
      }
      if (code === 'ArrowLeft' || code === 'ArrowRight') {
        const idx = menuOrder.indexOf(openMenu);
        if (idx < 0) return;
        const delta = code === 'ArrowRight' ? 1 : -1;
        const nextIdx = (idx + delta + menuOrder.length) % menuOrder.length;
        setOpenMenu(menuOrder[nextIdx]);
        setActiveIndex(0);
        // Optionally preview first item of new menu for immediate feedback
        previewAt(menuOrder[nextIdx], 0);
        return;
      }
      if (code === 'Enter') {
        commitAt(openMenu, activeIndex);
        return;
      }
      if (code === 'Escape') {
        setOpenMenu(null);
        resetPreview();
        return;
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [openMenu, activeIndex, menuOrder, onPreview, onCommit, onChangeDirection, onPreviewDirection]);
  return (
    <Menubar
      className='h-7 rounded-md px-1 py-0 text-[11px] gap-1'
      tabIndex={0}
      value={openMenu ?? ''}
      onValueChange={handleMenubarValueChange}>
      {/* Text color */}
      <MenubarMenu value='text'>
        <MenubarTrigger
          disabled={disabled}
          style={{ color: selected?.textColor ?? '#111827' }}
          title='Text color'
          className='h-7 w-7 px-0 py-0 inline-flex items-center justify-center'
          onMouseEnter={() => setOpenMenu('text')}
          onPointerEnter={() => setOpenMenu('text')}>
          Aa
        </MenubarTrigger>
        <MenubarContent
          side='top'
          align='center'
          className='min-w-fit p-1'
          onMouseEnter={ensureSnapshot}
          onMouseLeave={() => {
            if (!closingFromCommitRef.current) {
              resetPreview();
            }
            setOpenMenu(null);
          }}>
          <div className='flex flex-col gap-1'>
            {SWATCHES.map((c, i) => (
              <Button
                key={`t-${c}`}
                type='button'
                variant='ghost'
                size='sm'
                className={`rounded-sm px-1 py-1 text-[11px] flex items-center gap-2 ${
                  isActive('text', i) ? 'bg-muted' : 'hover:bg-muted'
                }`}
                aria-selected={isActive('text', i)}
                onMouseEnter={() => {
                  setActiveIndex(i);
                  onPreview?.({ textColor: c });
                }}
                onClick={() => commitStyleAndClose({ textColor: c })}>
                <span className='inline-flex h-4 w-4 items-center justify-center font-semibold' style={{ color: c }}>
                  Aa
                </span>
              </Button>
            ))}
          </div>
        </MenubarContent>
      </MenubarMenu>

      {/* Background color */}
      <MenubarMenu value='bg'>
        <MenubarTrigger
          disabled={disabled}
          title='Background color'
          className='h-7 w-7 px-0 py-0 inline-flex items-center justify-center'
          onMouseEnter={() => setOpenMenu('bg')}
          onPointerEnter={() => setOpenMenu('bg')}>
          <span
            className='inline-block h-4 w-4 rounded-full border'
            style={{ backgroundColor: selected?.bgColor ?? '#ffffff' }}
          />
        </MenubarTrigger>
        <MenubarContent
          side='top'
          align='center'
          className='min-w-fit p-1'
          onMouseEnter={ensureSnapshot}
          onMouseLeave={() => {
            if (!closingFromCommitRef.current) {
              resetPreview();
            }
            setOpenMenu(null);
          }}>
          <div className='flex flex-col gap-1'>
            {SWATCHES.map((c, i) => (
              <Button
                key={`bg-${c}`}
                type='button'
                variant='ghost'
                size='sm'
                className={`rounded-sm px-1 py-1 flex items-center gap-2 ${
                  isActive('bg', i) ? 'bg-muted' : 'hover:bg-muted'
                }`}
                aria-selected={isActive('bg', i)}
                onMouseEnter={() => {
                  setActiveIndex(i);
                  onPreview?.({ bgColor: c });
                }}
                onClick={() => commitStyleAndClose({ bgColor: c })}>
                <span className='inline-block h-4 w-4 rounded-full border' style={{ backgroundColor: c }} />
              </Button>
            ))}
          </div>
        </MenubarContent>
      </MenubarMenu>

      {/* Border color */}
      <MenubarMenu value='border'>
        <MenubarTrigger
          disabled={disabled}
          title='Border color'
          className='h-7 w-7 px-0 py-0 inline-flex items-center justify-center'
          onMouseEnter={() => setOpenMenu('border')}
          onPointerEnter={() => setOpenMenu('border')}>
          <span
            className='inline-block h-4 w-4 rounded-full border'
            style={{ borderColor: selected?.borderColor ?? '#9ca3af' }}
          />
        </MenubarTrigger>
        <MenubarContent
          side='top'
          align='center'
          className='min-w-fit p-1'
          onMouseEnter={ensureSnapshot}
          onMouseLeave={() => {
            if (!closingFromCommitRef.current) {
              resetPreview();
            }
            setOpenMenu(null);
          }}>
          <div className='flex flex-col gap-1'>
            {SWATCHES.map((c, i) => (
              <Button
                key={`bd-${c}`}
                type='button'
                variant='ghost'
                size='sm'
                className={`rounded-sm px-1 py-1 flex items-center gap-2 ${
                  isActive('border', i) ? 'bg-muted' : 'hover:bg-muted'
                }`}
                aria-selected={isActive('border', i)}
                onMouseEnter={() => {
                  setActiveIndex(i);
                  onPreview?.({ borderColor: c });
                }}
                onClick={() => commitStyleAndClose({ borderColor: c })}>
                <span className='inline-block h-4 w-4 rounded-full border' style={{ borderColor: c }} />
              </Button>
            ))}
          </div>
        </MenubarContent>
      </MenubarMenu>

      {/* Shape */}
      <MenubarMenu value='shape'>
        <MenubarTrigger
          disabled={disabled}
          title='Shape'
          className='h-7 w-7 px-0 py-0 inline-flex items-center justify-center'
          onMouseEnter={() => setOpenMenu('shape')}
          onPointerEnter={() => setOpenMenu('shape')}>
          {selected?.shape === 'rectangle'
            ? '▭'
            : selected?.shape === 'pill'
            ? '⬭'
            : selected?.shape === 'underline'
            ? '▁'
            : '◼︎'}
        </MenubarTrigger>
        <MenubarContent
          side='top'
          align='center'
          className='min-w-fit p-1'
          onMouseEnter={ensureSnapshot}
          onMouseLeave={() => {
            if (!closingFromCommitRef.current) {
              resetPreview();
            }
            setOpenMenu(null);
          }}>
          <div className='flex flex-col gap-1'>
            {(
              [
                { id: 'rounded', label: '◼︎' },
                { id: 'rectangle', label: '▭' },
                { id: 'pill', label: '⬭' },
                { id: 'underline', label: '▁' },
              ] as Array<{
                id: MindmapNodeShape;
                label: string;
              }>
            ).map((s, i) => (
              <Button
                key={s.id}
                type='button'
                variant='ghost'
                size='sm'
                className={`rounded-sm px-1 py-1 text-[14px] leading-4 text-center flex items-center justify-center ${
                  isActive('shape', i) ? 'bg-muted' : 'hover:bg-muted'
                }`}
                aria-selected={isActive('shape', i)}
                onMouseEnter={() => {
                  setActiveIndex(i);
                  onPreview?.({ shape: s.id });
                }}
                onClick={() => commitStyleAndClose({ shape: s.id })}>
                <span className='mx-auto'>{s.label}</span>
              </Button>
            ))}
          </div>
        </MenubarContent>
      </MenubarMenu>

      {isRoot && (
        <>
          <MenubarSeparator />
          <MenubarMenu value='direction'>
            <MenubarTrigger
              disabled={disabled}
              title='Layout direction'
              className='h-7 w-7 px-0 py-0 inline-flex items-center justify-center'
              onMouseEnter={() => setOpenMenu('direction')}
              onPointerEnter={() => setOpenMenu('direction')}>
              ↔︎
            </MenubarTrigger>
            <MenubarContent
              side='top'
              align='center'
              className='min-w-fit p-1'
              onMouseLeave={() => {
                if (!closingFromCommitRef.current) {
                  onResetDirectionPreview?.();
                }
                setOpenMenu(null);
              }}>
              <div className='flex flex-col gap-1'>
                {[
                  { key: 'left' as MindmapLayoutDirection, icon: '←', label: 'Layout left' },
                  { key: 'right' as MindmapLayoutDirection, icon: '→', label: 'Layout right' },
                  { key: 'up' as MindmapLayoutDirection, icon: '↑', label: 'Layout up' },
                  { key: 'down' as MindmapLayoutDirection, icon: '↓', label: 'Layout down' },
                ].map(({ key, icon }, i) => (
                  <Button
                    key={key}
                    type='button'
                    className={`rounded-sm px-1 py-1 text-[11px] leading-4 flex items-center justify-center ${
                      isActive('direction', i) ? 'bg-muted' : 'hover:bg-muted'
                    }`}
                    aria-selected={isActive('direction', i)}
                    onMouseEnter={() => {
                      setActiveIndex(i);
                      onPreviewDirection?.(key);
                    }}
                    onClick={() => commitDirectionAndClose(key)}
                    variant='ghost'
                    size='sm'>
                    {icon}
                  </Button>
                ))}
              </div>
            </MenubarContent>
          </MenubarMenu>
        </>
      )}
    </Menubar>
  );
}
export default NodeStyleMenubar;
